import type { MigrationVersion } from "@/api/migrationService";

declare function showDirectoryPicker(options?: { mode?: "read" | "readwrite" }): Promise<FileSystemDirectoryHandle>;

/**
 * Faster than `Uint8Array.from(atob(b64), ch => ch.charCodeAt(0))`:
 * - Avoids per-character callback overhead (3-10x faster on large files)
 * - Reduces main-thread blocking for multi-MB Excel files
 */
export const base64ToUint8Array = (b64: string): Uint8Array => {
  const bstr = atob(b64);
  const len = bstr.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = bstr.charCodeAt(i);
  return bytes;
};

export const pickDirectory = (): Promise<FileSystemDirectoryHandle> =>
  showDirectoryPicker({ mode: "readwrite" });

export const toFileName = (
  partId: number,
  version: MigrationVersion,
  supplierCode: string,
): string => `${partId}_${version}_${supplierCode}.xlsx`;

export type ParsedFileName = {
  partId: number;
  version: MigrationVersion;
  supplierCode: string;
};

export const parseFileName = (name: string): ParsedFileName | null => {
  const m = name.match(/^(\d+)_(v1|v2)_(.+)\.xlsx$/);
  if (!m) return null;
  return { partId: +m[1], version: m[2] as MigrationVersion, supplierCode: m[3] };
};

export const writeXlsx = async (
  dir: FileSystemDirectoryHandle,
  name: string,
  bytes: Uint8Array,
): Promise<void> => {
  const fh = await dir.getFileHandle(name, { create: true });
  const w = await fh.createWritable();
  await w.write(bytes);
  await w.close();
};

export const fileExists = async (
  dir: FileSystemDirectoryHandle,
  name: string,
): Promise<boolean> => {
  try {
    await dir.getFileHandle(name);
    return true;
  } catch {
    return false;
  }
};

export const listXlsx = async (
  dir: FileSystemDirectoryHandle,
): Promise<string[]> => {
  const out: string[] = [];
  for await (const entry of (dir as unknown as { values(): AsyncIterable<FileSystemHandle> }).values()) {
    if (entry.kind === "file" && entry.name.endsWith(".xlsx")) out.push(entry.name);
  }
  return out;
};

export const readAsFile = async (
  dir: FileSystemDirectoryHandle,
  name: string,
): Promise<File> => {
  const fh = await dir.getFileHandle(name);
  return fh.getFile();
};

export const deleteXlsx = async (
  dir: FileSystemDirectoryHandle,
  name: string,
): Promise<void> => {
  await dir.removeEntry(name);
};

// ─── Exchange Rate migration (per-supplier, not per-part) ──────────────────────
// Prefix `er_` keeps files disjoint from part-migration filenames
// (`{partId}_{v1|v2}_{supplierCode}.xlsx`) if the same folder is reused.

export const toExchangeRateFileName = (supplierCode: string): string =>
  `er_${supplierCode}.xlsx`;

export type ParsedExchangeRateFileName = {
  supplierCode: string;
};

export const parseExchangeRateFileName = (
  name: string,
): ParsedExchangeRateFileName | null => {
  const m = name.match(/^er_(.+)\.xlsx$/);
  if (!m) return null;
  return { supplierCode: m[1] };
};
